import TicketDetails, {
  BetDetailItem,
} from '@/ui-components/components/TicketDetails'
import { useTranslation } from 'react-i18next'
import useLayout from '@/hooks/useLayout'
import ThemeToggle from '@/ui-components/components/ThemeToggle/ThemeToggle'
import { useNavigate, useParams } from 'react-router'
import { useGetTicketViewOnly } from './hooks/useGetTicketViewOnly'
import LoadingScreen from '@/ui-components/components/LoadingScreen'
import { useTheme } from '@/ui-components/context/ThemeProvider'
import { useEffect, useRef } from 'react'
import { useGameUtils } from '@/utils/gameUtils'
import { Game, Round } from '@/redux/reducers/gameReducer'
import { getAsTicketStatus } from '@/ui-components/components/TicketStatusCard/utils'
import { convertToDateTime } from '@/utils/date-time-utils'
import { DATE_TIME_FORMAT_HUMAN } from '@/utils/date-time-utils'
import { getLottoConfig } from '@/utils/lotto-type-v2'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { useThemeAssets } from '@/ui-components/hooks/useThemeAssets'
import { getThemeMode } from '@/ui-components/utils/theme-utils'
import { ThemeMode } from '@/ui-components/constants/theme'

const TicketDetailsPage = () => {
  const navigate = useNavigate()
  const { transactionId } = useParams()
  const { theme, setTheme } = useTheme()
  const { svgAssets } = useThemeAssets()
  const response = useGetTicketViewOnly(transactionId)
  const { getGameName, getDetailName } = useGameUtils()
  const { t } = useTranslation()
  const { onLanguageChange, currentLang } = useLayout()

  // Logo logic following the same pattern as other files
  // Get theme mode
  const themeMode = getThemeMode(theme)

  // Get default logo based on theme mode (using dynamic assets from useThemeAssets)
  let showLogo = svgAssets.LOGO[themeMode]

  // Override with custom logo if provided
  if (themeMode === ThemeMode.DARK) {
    if (
      response.ticketData?.logo_url_dark &&
      response.ticketData?.logo_url_dark !== ''
    ) {
      showLogo = response.ticketData.logo_url_dark
    } else if (
      response.ticketData?.logo_url &&
      response.ticketData?.logo_url !== ''
    ) {
      showLogo = response.ticketData.logo_url
    }
  }
  if (themeMode === ThemeMode.LIGHT) {
    if (response.ticketData?.logo_url && response.ticketData?.logo_url !== '') {
      showLogo = response.ticketData.logo_url
    } else if (
      response.ticketData?.logo_url_dark &&
      response.ticketData?.logo_url_dark !== ''
    ) {
      showLogo = response.ticketData.logo_url_dark
    }
  }
  const selectedTheme = response.ticketData?.theme ?? theme
  const selectedLanguage = response.ticketData?.language ?? currentLang
  const hasSetInitialTheme = useRef(false)

  useEffect(() => {
    if (
      selectedLanguage &&
      selectedLanguage !== currentLang &&
      onLanguageChange
    ) {
      onLanguageChange(selectedLanguage)
    }
  }, [selectedLanguage, currentLang, onLanguageChange])

  useEffect(() => {
    if (
      selectedTheme &&
      selectedTheme !== theme &&
      response.ticketData &&
      !hasSetInitialTheme.current
    ) {
      setTheme(selectedTheme)
      hasSetInitialTheme.current = true
    }
  }, [response.ticketData, selectedTheme, theme, setTheme])

  const ticketData = response.ticketData?.ticket

  const lottoRoundGame = response.ticketData?.ticket?.lotto_round_game as
    | Round
    | undefined

  const configs = getLottoConfig(lottoRoundGame as Round | undefined)

  // Get winning numbers from results object
  const results = response.ticketData?.ticket?.results

  const betDetails: BetDetailItem[] = useMemo(() => {
    const pollDetails = response.ticketData?.ticket?.details ?? []
    if (!pollDetails) return []

    return pollDetails.map((item, index) => {
      const type = item.type as string
      const reward = configs.find((c) => c.type === type)?.reward ?? 0
      let limitReward: number | undefined = undefined
      if (reward !== item.reward_amount) {
        limitReward = item.reward_amount
      }
      const status = getAsTicketStatus(
        item.status ?? 'waiting',
        ticketData?.cancellation_reason,
      )

      // Calculate final payout: effective payout rate × bet amount
      const effectivePayoutRate = limitReward ?? reward
      const betAmount = item.bet_amount ?? 0
      const calculatedFinalPayout = effectivePayoutRate * betAmount

      // Get winning numbers for this bet type from results
      const winningNumbers = results?.[type as keyof typeof results]
      // Format winning numbers: join with comma/newline if multiple, or use first one
      const rewardResult =
        winningNumbers && winningNumbers.length > 0
          ? winningNumbers.join(', ')
          : item.reward_result

      return {
        betType: t(`betDetailPanel.betLottoType.${type}`) || type,
        rawBetType: type, // Pass original bet type for internal logic
        rowNumber: index + 1,
        status: status,
        betNumber: item.number,
        rewardResult: rewardResult,
        payoutRate: reward,
        limitedPayoutRate: limitReward,
        betAmount: betAmount,
        finalPayout: calculatedFinalPayout,
      }
    })
  }, [
    response.ticketData?.ticket?.details,
    results,
    configs,
    t,
    ticketData?.cancellation_reason,
  ])

  if (response.isLoading) {
    return <LoadingScreen />
  }
  if (response.error) {
    toast.error(response.error)
    navigate('/404')
    return
  }
  if (!transactionId || transactionId === '') {
    navigate('/404')
    return
  }

  return (
    <div className="bg-[var(--bg-theme)] min-h-screen flex flex-col">
      {/* Custom Navbar - Only Logo, Language, Theme */}
      <nav className="bg-[var(--navbar-bg)] shadow-sm sticky top-0 z-20">
        <div className="flex items-center justify-between w-full max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-12 xl:px-20 2xl:px-0 py-4">
          {/* Logo */}
          <img
            src={showLogo || ''}
            alt={t('navbar.logoAlt')}
            className="h-8 sm:h-10 w-20 sm:w-24 object-contain cursor-pointer"
            onClick={() => {}}
          />

          {/* Right Section - Only Language & Theme */}
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </div>
      </nav>

      {/* Main content without bottom navigation */}
      <main className="flex-grow relative">
        <div className="flex flex-col justify-center items-center max-w-[90rem] mx-auto px-4 sm:px-8 lg:px-16 xl:px-24 2xl:px-0 py-8">
          {/* Simple title without back button */}
          <div className="w-full max-w-3xl mb-6">
            <h2 className="text-2xl font-bold text-[var(--card-title)] text-center">
              {t('pollItem.details')}
            </h2>
          </div>

          {/* Ticket details */}
          <TicketDetails
            ticketNumber={ticketData?.id || 0}
            betDate={convertToDateTime(
              ticketData?.created_at || '',
              DATE_TIME_FORMAT_HUMAN,
              currentLang,
            )}
            listTitle={getGameName(ticketData?.lotto_game as Game | undefined)}
            drawDate={getDetailName(
              ticketData?.lotto_game as Game | undefined,
              ticketData?.lotto_round_game as Round | undefined,
            )}
            amount={ticketData?.bet_amount || 0}
            winAmount={ticketData?.win_amount || 0}
            status={getAsTicketStatus(
              ticketData?.status || 'waiting',
              ticketData?.cancellation_reason,
            )}
            canCancel={false}
            betDetails={betDetails}
          />
        </div>
      </main>
    </div>
  )
}

export default TicketDetailsPage
